import { findCountryById, findStateById } from "../countrystateutils";
import { findEnumObject } from "../enumutils";
import { parseFromTime } from "../momentutils";
import { createFieldMetaData } from "./createFieldMetaData";
import type { FieldMetaData } from "./fieldsMetaData";

export interface DoorsFormState {
  open: moment.Moment;
  close: moment.Moment;
}

export interface NameAndAddress extends Address {
  companyName: string;
}

export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: server.dto.State;
  country: server.dto.Country;
}

export interface PostalAndVisitingAddress {
  visitingAddress: NameAndAddress;
  postalAddress: NameAndAddress;
}

export interface CompanyProfile {
  isArtist: server.dto.IsArtistTypes;
  isPromoter: server.dto.IsPromoterTypes;
  isVenue: server.dto.IsVenueTypes;
}

export interface CompanyFormState {
  address: PostalAndVisitingAddress;
  profile: CompanyProfile;
  status: server.dto.StatusSmall;
  website: string;
  phone: string;
  facebook: string;
  networkPath: string;
  additional: string;
  financialRisk: server.dto.FinancialRisk;
  euVatNumber: string;
  globalLocationNumber: string;
  fiscalNumber1: string;
  fiscalNumber2: string;
  businessRegistrationNumber: string;
  owner: server.dto.UserSmall
  internalId: string;
  legalStructure: string;
  expectPurchaseOrderNumber: boolean;
}

export interface VenueFormState {
  capacity: number;
  ageRestriction: server.dto.AgeRestriction;
  doors: DoorsFormState;
  category: server.dto.VenueCategory;
  stage: server.dto.Stage
  vehicleAccess: string;
  parking: string;
  gearAccess: string;
  power: string;
  lighting: string;
  dressingRooms: string;
  noiseRestrictions: string;
}

export interface BankFormState {
  accountNumber: string;
  beneficiaryName: string;
  swiftCode: string;
  sortCode: string;
  bankAddress: string;
}

export const convertToPhoneNumbers = (value: string): string[] => {
  return value.split(/,|;/).map(num => num.trim()).filter(num => num.length > 0)
}

export const convertToNameAndAddress = (companyName: string, address: server.dto.Address, serverReference: server.dto.ServerReference): NameAndAddress => ({
  companyName: companyName ?? "",
  addressLine1: address.addressLine1 ?? "",
  addressLine2: address.addressLine2 ?? "",
  city: address.city ?? "",
  postalCode: address.postalCode ?? "",
  // @ts-ignore
  state: findStateById(address.stateId, serverReference),
  // @ts-ignore
  country: findCountryById(address.country, serverReference),
});

export const convertToFormState = (company: server.dto.Company, serverReference: server.dto.ServerReference): CompanyFormState => ({
  address: {
    visitingAddress: convertToNameAndAddress(company.name, company.address, serverReference),
    postalAddress: convertToNameAndAddress(company.admName, company.admAddress, serverReference),
  },
  profile: {
    isArtist: company.isArtist,
    isPromoter: company.isPromoter,
    isVenue: company.isVenue,
  },
  status: company.status,
  website: company.website ?? "",
  phone: company.landlineNumbers.join(";"),
  facebook: company.facebook ?? "",
  networkPath: company.networkPath ?? "",
  additional: company.additional ?? "",
  financialRisk: company.financialRisk ?? {
    comment: "",
    level: "unknown",
  },
  euVatNumber: company.euVatNumber?.number ?? "",
  fiscalNumber1: company.fiscalNumber1 ?? "",
  fiscalNumber2: company.fiscalNumber2 ?? "",
  businessRegistrationNumber: company.businessRegistrationNumber ?? "",
  owner: company.owner,
  internalId: company.internalId ?? "",
  legalStructure: company.legalStructure ?? "",
  expectPurchaseOrderNumber: company.expectPurchaseOrderNumber,
  globalLocationNumber: company.globalLocationNumber ?? "",
});

export const convertToVenueFormState = (
    venueDetails: server.dto.VenueDetails,
    serverReference: server.dto.ServerReference,
): VenueFormState => ({
  capacity: venueDetails?.capacity,
  // @ts-ignore
  ageRestriction: venueDetails?.ageRestriction ? findEnumObject(venueDetails.ageRestriction, serverReference.ageRestrictions) : findEnumObject("unknown", serverReference.ageRestrictions),
  doors: {
    // @ts-ignore
    open: venueDetails?.doors?.open ? parseFromTime(venueDetails.doors.open) : null,
    // @ts-ignore
    close: venueDetails?.doors?.close ? parseFromTime(venueDetails.doors.close) : null,
  },
  // @ts-ignore
  category: venueDetails?.category ? findEnumObject(venueDetails.category, serverReference.venueCategories) : findEnumObject("unknown", serverReference.venueCategories),
  stage: {
    // @ts-ignore
    width: venueDetails?.stage?.width,
    // @ts-ignore
    depth: venueDetails?.stage?.depth,
    // @ts-ignore
    toFloor: venueDetails?.stage?.toFloor,
    // @ts-ignore
    toCeiling: venueDetails?.stage?.toCeiling,
    formatted: []
  },
  vehicleAccess: venueDetails?.vehicleAccess ?? "",
  parking: venueDetails?.parking ?? "",
  gearAccess: venueDetails?.gearAccess ?? "",
  power: venueDetails?.power ?? "",
  lighting: venueDetails?.lighting ?? "",
  dressingRooms: venueDetails?.dressingRooms ?? "",
  noiseRestrictions: venueDetails?.noiseRestrictions ?? "",
})

export const convertToBankFormState = (bankDetails: server.dto.CompanyBank): BankFormState => ({
  accountNumber: bankDetails.accountNumber ?? "",
  beneficiaryName: bankDetails.beneficiaryName ?? "",
  swiftCode: bankDetails.swiftCode ?? "",
  sortCode: bankDetails.sortCode ?? "",
  bankAddress: bankDetails.bankAddress ?? "",
});

export const getInstruction = (
  company: server.dto.Company,
  companyForm: CompanyFormState,
  bankForm: BankFormState,
  partial: Partial<server.dto.UpdateCompanyDetailsInstruction>
): server.dto.UpdateCompanyDetailsInstruction => ({
  additional: companyForm.additional,
  administrativeAddress: {
    addressLine1: companyForm.address.postalAddress.addressLine1,
    addressLine2: companyForm.address.postalAddress.addressLine2,
    city: companyForm.address.postalAddress.city,
    postalCode: companyForm.address.postalAddress.postalCode,
    stateId: companyForm.address.postalAddress.state.key
  },
  regularAddress: {
    addressLine1: companyForm.address.visitingAddress.addressLine1,
    addressLine2: companyForm.address.visitingAddress.addressLine2,
    city: companyForm.address.visitingAddress.city,
    postalCode: companyForm.address.visitingAddress.postalCode,
    stateId: companyForm.address.visitingAddress.state.key
  },
  regularName: companyForm.address.visitingAddress.companyName,
  administrativeName: companyForm.address.postalAddress.companyName,
  bankDetails: {
    accountNumber: bankForm.accountNumber,
    beneficiaryName: bankForm.beneficiaryName,
    swiftCode: bankForm.swiftCode,
    sortCode: bankForm.sortCode,
    bankAddress: bankForm.bankAddress
  },
  businessRegistrationNumber: companyForm.businessRegistrationNumber,
  companyId: company.companyId,
  euVatNumber: companyForm.euVatNumber,
  expectPurchaseOrderNumber: companyForm.expectPurchaseOrderNumber,
  facebook: companyForm.facebook,
  financialRisk: {
    comment: companyForm.financialRisk?.comment,
    level: companyForm.financialRisk?.level,
  },
  fiscalNumber1: companyForm.fiscalNumber1,
  fiscalNumber2: companyForm.fiscalNumber2,
  geoLocation: company.geoCode?.location,
  googlePlaceIdentifier: company.googlePlaceIdentifier,
  industryClassification: company.industryClassification,
  internalId: companyForm.internalId,
  isArtist: companyForm.profile.isArtist,
  isPromoter: companyForm.profile.isPromoter,
  isVenue: companyForm.profile.isVenue,
  legalStructure: companyForm.legalStructure,
  networkPath: companyForm.networkPath,
  ownerUserId: companyForm.owner.userId,
  phoneNumbers: convertToPhoneNumbers(companyForm.phone),
  statusId: companyForm.status.statusId,
  website: companyForm.website,
  globalLocationNumber: companyForm.globalLocationNumber,
  ...partial,
});

export const getUpdateVenueInstuction = (companyId: number, venue: VenueFormState, partial: Partial<server.dto.SetVenueDetailsInstruction>): server.dto.SetVenueDetailsInstruction => ({
  companyId,
  ageRestriction: venue.ageRestriction?.value,
  capacity: venue.capacity,
  category: venue.category?.value,
  // @ts-ignore
  doors: venue.doors ? {
    open: venue.doors.open?.format("HH:mm"),
    close: venue.doors.close?.format("HH:mm")
  } : undefined,
  dressingRooms: venue.dressingRooms,
  gearAccess: venue.gearAccess,
  lighting: venue.lighting,
  noiseRestrictions: venue.noiseRestrictions,
  parking: venue.parking,
  power: venue.power,
  stage: venue.stage,
  vehicleAccess: venue.vehicleAccess,
  ...partial,
});

export const getUpdateBankAccountInstruction = (company: server.dto.Company, companyFormState: CompanyFormState, bankAccountFormState: BankFormState, partial: Partial<server.dto.UpdateBankAccountInstruction>): server.dto.UpdateCompanyDetailsInstruction => {
  const instruction = getInstruction(company, companyFormState, bankAccountFormState, {});

  return {
    ...instruction,
    bankDetails: {
      ...instruction.bankDetails,
      accountNumber: partial.accountNumber ?? instruction!.bankDetails!.accountNumber,
      bankAddress: partial.bankAddress ?? instruction!.bankDetails!.bankAddress,
      beneficiaryName: partial.beneficiaryName ?? instruction!.bankDetails!.beneficiaryName,
      sortCode: partial.sortCode ?? instruction!.bankDetails!.sortCode,
      swiftCode: partial.swiftCode ?? instruction!.bankDetails!.swiftCode,
    }
  }
};

export interface CompanyFieldMetaData {
    additionInformation: FieldMetaData;
    euVatNumber: FieldMetaData;
    facebook: FieldMetaData;
    map: FieldMetaData;
    owner: FieldMetaData;
    phone: FieldMetaData;
    postalAddress: FieldMetaData;
    profile: FieldMetaData;
    status: FieldMetaData;
    tags: FieldMetaData;
    visitingAddress: FieldMetaData;
    website: FieldMetaData;
}

export const getCompanyFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    item: server.dto.Company, //This should probs be the shared CompanyFormState object
    itemPermissions: server.dto.CompanyObjectPermissions,
    hasTags: boolean,
): CompanyFieldMetaData => ({
    additionInformation: createFieldMetaData(
        !item.additional,
        environment.type === "external" || (!item.additional && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    euVatNumber: createFieldMetaData(
        !item.euVatNumber.number,
        (!item.euVatNumber.number && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    facebook: createFieldMetaData(
        !item.facebook,
        (!item.facebook && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    map: createFieldMetaData(
        !item.geoCode.location,
        !item.geoCode.location,
        false
    ),

    owner: createFieldMetaData(
        false,
        environment.type === "external", //Dont display to external users,
        !itemPermissions.canModify,
    ),

    phone: createFieldMetaData(
        !item.landlineNumbers?.length,
        !item.landlineNumbers?.length && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    postalAddress: createFieldMetaData(
        false,
        false,
        environment.type === "external" || !itemPermissions.canModify,
    ),

    profile: createFieldMetaData(
        item.isArtist === "no" && item.isVenue === "no" && item.isPromoter === "no",
        (item.isArtist === "no" && item.isVenue === "no" && item.isPromoter === "no") && !itemPermissions.canModify,
        !itemPermissions.canModify,
    ),

    status: createFieldMetaData(
        false,
        environment.type === "external", //Dont display to external users
        !itemPermissions.canModify,
    ),

    tags: createFieldMetaData(
        !item.tags || item.tags.length === 0,
        !hasTags || environment.type === "external" || ((!item.tags || item.tags.length === 0) && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    visitingAddress: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    website: createFieldMetaData(
        !item.website,
        (!item.website && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
});